import React, { useState, useEffect } from "react";
import { Modal, Button, Badge, Alert } from "react-bootstrap";
import { obtenerDeudaPendienteCliente, esUsuarioPropietario } from "../services/deudaClienteService";

const EMPTY_DEUDA_STATE = {
  facturas: [],
  totalDeuda: 0,
  totalesPorMoneda: {},
  tieneDeudaVencida: false,
  fechaLimitePagoIso: null,
  plazoPagoVencido: false,
  bloquearContinuar: false,
  fechaCorteBloqueoIso: null,
  puedeCerrarModalPorPago: false,
  enPeriodoAviso: false,
  diasHastaCorteMinimo: null,
};

/**
 * Hook personalizado para verificar deudas pendientes del cliente
 * @param {string} rucCliente - RUC del cliente
 * @param {number} diasVencimiento - Días mínimos de vencimiento (default: 45, usado solo si no hay fecha_corte)
 * @param {number} diasAvisoCorte - Días antes de la fecha_corte para mostrar aviso (default: 5)
 */
export const useDeudaPendiente = (rucCliente, diasVencimiento = 45, diasAvisoCorte = 5) => {
  const [loading, setLoading] = useState(true);
  const [deuda, setDeuda] = useState(EMPTY_DEUDA_STATE);

  useEffect(() => {
    const verificarDeuda = async () => {
      if (!rucCliente || esUsuarioPropietario(rucCliente)) {
        setDeuda(EMPTY_DEUDA_STATE);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const resultado = await obtenerDeudaPendienteCliente(rucCliente, diasVencimiento, diasAvisoCorte);
        setDeuda({ ...EMPTY_DEUDA_STATE, ...resultado });
      } catch (error) {
        console.error("Error verificando deuda:", error);
        setDeuda(EMPTY_DEUDA_STATE);
      } finally {
        setLoading(false);
      }
    };

    verificarDeuda();
  }, [rucCliente, diasVencimiento, diasAvisoCorte]);

  return { loading, ...deuda };
};

/**
 * Calcula la fecha límite sumando días hábiles (lunes a sábado)
 * @param {number} diasHabiles - Número de días hábiles a sumar (default: 5)
 * @returns {string} Fecha formateada de la fecha límite
 */
const obtenerFechaLimite = (diasHabiles = 5) => {
  const fecha = new Date();
  let diasContados = 0;
  
  while (diasContados < diasHabiles) {
    fecha.setDate(fecha.getDate() + 1);
    const diaSemana = fecha.getDay();
    // Solo contar días hábiles (lunes=1 a sábado=6, excluir domingo=0)
    if (diaSemana >= 1 && diaSemana <= 6) {
      diasContados++;
    }
  }
  
  return fecha.toLocaleDateString("es-PE", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatearFechaLarga = (isoDate) => {
  if (!isoDate) return null;
  const [year, month, day] = String(isoDate).split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("es-PE", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatearFechaCorta = (isoDate) => {
  if (!isoDate) return "-";
  const [year, month, day] = String(isoDate).split("-").map(Number);
  if (!year || !month || !day) return "-";
  return new Date(year, month - 1, day).toLocaleDateString("es-PE");
};

const formatearFechaEmision = (fecha) => {
  const value = String(fecha || "").trim();
  if (!value) return "-";

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
  }

  return value;
};

/**
 * Modal de advertencia de deuda pendiente
 * Se muestra a clientes que tienen facturas vencidas hace más de 45 días
 */
const ModalDeudaPendiente = ({ usuario }) => {
  const [mostrar, setMostrar] = useState(false);

  const {
    loading,
    facturas,
    tieneDeudaVencida,
    fechaLimitePagoIso,
    plazoPagoVencido,
    bloquearContinuar,
    fechaCorteBloqueoIso,
    puedeCerrarModalPorPago,
    enPeriodoAviso,
    diasHastaCorteMinimo,
  } = useDeudaPendiente(
    usuario?.ruc,
    15, // días de vencimiento mínimo para activar el modal (fallback sin fecha_corte)
    5   // días antes de fecha_corte para mostrar aviso
  );

  useEffect(() => {
    if (puedeCerrarModalPorPago) {
      setMostrar(false);
      return;
    }

    if (!loading && tieneDeudaVencida) {
      setMostrar(true);
    }
  }, [loading, tieneDeudaVencida, puedeCerrarModalPorPago]);

  const handleCerrar = () => {
    if (bloquearContinuar && usuario?.rol !== "superAdmin") return;
    setMostrar(false);
  };

  if (loading || !tieneDeudaVencida) {
    return null;
  }

  const facturasCriticas = facturas.filter((f) => f.esCritica);
  const fechaLimite = formatearFechaLarga(fechaLimitePagoIso) || obtenerFechaLimite(5);
  const fechaCorteBloqueo = formatearFechaLarga(fechaCorteBloqueoIso);

  return (
    <Modal
      show={mostrar}
      onHide={handleCerrar}
      centered
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header className="bg-danger text-white border-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-exclamation-triangle-fill fs-4"></i>
          <span>⚠️ Aviso Importante - Deuda Pendiente</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-4 py-4">
        <Alert variant="warning" className="mb-4 border-2">
          <div className="d-flex align-items-start gap-3">
            <i className="bi bi-clock-history fs-3 text-warning"></i>
            <div>
              <h5 className="mb-2">Estimado cliente,</h5>
              <p className="mb-0">
                Le informamos que su cuenta presenta <strong>{facturasCriticas.length} factura{facturasCriticas.length !== 1 ? "s" : ""} con vencimiento crítico</strong> y <strong>{facturas.length} pendiente{facturas.length !== 1 ? "s" : ""}</strong> de pago en total.
              </p>
            </div>
          </div>
        </Alert>
        <div className="mb-4">
          <h6 className="text-muted mb-3">Detalle de facturas pendientes:</h6>
          <div className="table-responsive">
            <table className="table table-hover table-sm align-middle">
              <thead className="table-light">
                <tr>
                  <th>Documento</th>
                  <th>Fecha Emisión</th>
                  <th>Fecha Corte</th>
                  <th className="text-center">Días Vencido</th>
                </tr>
              </thead>
              <tbody>
                {facturas.slice(0, 5).map((factura) => (
                  <tr key={factura.id}>
                    <td>
                      <Badge bg="secondary" className="me-1">
                        {factura.tipo}
                      </Badge>
                      {factura.serie}-{factura.numero}
                    </td>
                    <td>{formatearFechaEmision(factura.fechaEmision)}</td>
                    <td>{formatearFechaCorta(factura.fechaCorteIso)}</td>
                    <td className="text-center">
                      <Badge 
                        bg={factura.diasVencido > 60 ? "danger" : "warning"}
                        className="px-3"
                      >
                        {factura.diasVencido} días
                      </Badge>
                    </td>
                  </tr>
                ))}
                {facturas.length > 5 && (
                  <tr>
                    <td colSpan="4" className="text-center text-muted fst-italic">
                      ... y {facturas.length - 5} factura(s) más
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <Alert variant="danger" className="mb-0 border-0" style={{ background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)" }}>
          <div className="d-flex align-items-start gap-3">
            <i className="bi bi-exclamation-triangle-fill fs-3 text-danger"></i>
            <div>
              <h6 className="mb-2 fw-bold text-danger">Recordatorio de Pago</h6>
              <p className="mb-2 text-dark">
                Mantener nuestras cuentas al día nos permite <strong>cumplir con nuestra cadena de pagos</strong> y seguir brindándole un servicio de calidad. Su colaboración es fundamental para el correcto funcionamiento de nuestras operaciones.
              </p>
              {bloquearContinuar ? (
                <p className="mb-0 text-danger small fw-bold">
                  Su acceso está suspendido desde el <strong>{fechaCorteBloqueo || "día de corte"}</strong> por superar la fecha de corte de pago. Regularice su deuda para reactivar el acceso a la plataforma.
                </p>
              ) : enPeriodoAviso ? (
                <p className="mb-0 text-danger small fw-bold">
                  Faltan <strong>{diasHastaCorteMinimo}</strong> día{diasHastaCorteMinimo !== 1 ? "s" : ""} para la fecha de corte de pago ({fechaCorteBloqueo || "fecha de corte"}). Regularice su pago para evitar la suspensión del servicio.
                </p>
              ) : plazoPagoVencido ? (
                <p className="mb-0 text-danger small fw-bold">
                  El plazo de 5 días hábiles para regularizar el pago venció el <strong>{fechaLimite}</strong>. Procederemos con la suspensión temporal del acceso a la plataforma hasta regularizar la situación.
                </p>
              ) : (
                <p className="mb-0 text-muted small">
                  Le agradecemos regularizar el pago en un plazo máximo de <strong>5 días hábiles (hasta el {fechaLimite})</strong>. De no concretarse, lamentablemente deberemos suspender temporalmente el acceso a la plataforma hasta regularizar la situación.
                </p>
              )}
            </div>
          </div>
        </Alert>
      </Modal.Body>

      <Modal.Footer className="border-0 px-4 pb-4">
        <Button
          variant={(bloquearContinuar && usuario?.rol !== "superAdmin") ? "secondary" : "success"}
          onClick={handleCerrar}
          disabled={bloquearContinuar && usuario?.rol !== "superAdmin"}
          className="w-100"
        >
          {(bloquearContinuar && usuario?.rol !== "superAdmin")
            ? "Acceso bloqueado por suspensión del servicio"
            : (puedeCerrarModalPorPago
              ? "Pago registrado, continuar a la plataforma"
              : "Entendido, continuar a la plataforma")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalDeudaPendiente;
